import React, { FC, useEffect } from 'react';
import { Row, Col, Form, Select as BaseSelect, Alert } from 'antd';
import { DatePicker, Input, Select } from '@cloudcar-app/cloudcar-components';
import { formatRut, validateRut } from '@abstract-cl/rut-ts';
import { FormInstance } from 'antd/es/form/Form';
import rutFormValidator from 'utils/rutFormValidator';
import { Customer, Insurance } from 'apollo/types';
import communes from 'utils/comunes';
import dateFormValidator from 'utils/dateFormValidator';
import { InfoCircleOutlined } from '@ant-design/icons';
import styles from './InsuranceProposalFormModal.module.scss';

const paymentDays = [
  {
    key: '5',
    value: '5',
  },
  {
    key: '15',
    value: '15',
  },
  {
    key: '25',
    value: '25',
  },
];

interface ZurichModalProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: FormInstance<any>;
  insuranceData?: Insurance;
  customerData?: Customer;
}

const ZurichModal: FC<ZurichModalProps> = (props: ZurichModalProps) => {
  const { form, insuranceData, customerData } = props;
  const { Option } = BaseSelect;

  const handleCustomerChangeRut = (rut: string): string => {
    if (validateRut(rut)) {
      const formatted: string = formatRut(rut) || '';
      form.setFieldsValue({ rut: formatted });
      return rut;
    }
    form.setFieldsValue({
      rut: rut.split('.').join('').split('-').join(''),
    });
    return rut;
  };

  useEffect(() => {
    if (insuranceData?.insuranceInput) {
      form.setFieldsValue({
        insuredName:
          insuranceData.insuranceInput.businessName ||
          insuranceData.insuranceInput.name,
        insuredFirstSurname: insuranceData.insuranceInput.businessName
          ? '.'
          : insuranceData.insuranceInput.firstSurname,
        insuredEmail: insuranceData.insuranceInput.email,
        insuredPhone: insuranceData.insuranceInput.phone,
        insuredRut: insuranceData.insuranceInput.rut,
      });
    }
  }, [insuranceData]);

  useEffect(() => {
    if (customerData) {
      form.setFieldsValue({
        rut: customerData.rut,
      });
    }
  }, [insuranceData, customerData]);

  return (
    <>
      <Row gutter={[0, 12]}>
        <Col xs={24}>
          <h3 className={styles.SubTitle}>Datos del cliente</h3>
        </Col>
        <Col xs={24}>
          {insuranceData?.insuranceInput?.rut !== customerData?.rut && (
            <>
              <p style={{ marginBottom: '12px' }}> Contratante </p>
              <Row gutter={[12, 12]} style={{ alignItems: 'center' }}>
                <Col xs={12}>
                  <Form.Item
                    name="name"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el nombre del contratante',
                      },
                    ]}
                  >
                    <Input placeholder="Nombre" compact allowClear />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="firstSurname"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el apellido del contratante',
                      },
                    ]}
                  >
                    <Input placeholder="Apellido" compact allowClear />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="email"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el correo del contratante',
                      },
                    ]}
                  >
                    <Input
                      placeholder="Correo electrónico"
                      compact
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="rut"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el rut',
                      },
                      {
                        validator: rutFormValidator,
                      },
                    ]}
                  >
                    <Input
                      compact
                      placeholder="Rut"
                      allowClear
                      onChange={(e) => handleCustomerChangeRut(e.target.value)}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="gender"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el género del contratante',
                      },
                    ]}
                  >
                    <Select
                      label="Género"
                      placeholder="Género"
                      required
                      compact
                      allowClear
                    >
                      {[
                        {
                          key: 'M',
                          value: 'Masculino',
                        },
                        {
                          key: 'F',
                          value: 'Femenino',
                        },
                      ].map((item) => (
                        <Option key={item.key} value={item.key}>
                          {item.value}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="addressType"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el tipo de dirección',
                      },
                    ]}
                  >
                    <Select
                      label="Tipo de dirección"
                      placeholder="Tipo de dirección"
                      required
                      compact
                      allowClear
                    >
                      {[
                        {
                          key: 'P',
                          value: 'Particular',
                        },
                        {
                          key: 'C',
                          value: 'Cobro',
                        },
                      ].map((item) => (
                        <Option key={item.key} value={item.key}>
                          {item.value}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="phone"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el telefono del contratante',
                      },
                    ]}
                  >
                    <Input placeholder="Telefono" required compact allowClear />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="birthday"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        type: 'date',
                        message: 'Ingrese la fecha de nacimiento',
                      },
                      {
                        validator: dateFormValidator,
                      },
                    ]}
                  >
                    <DatePicker
                      className={styles.DatePicker}
                      placeholder="Fecha de nacimiento"
                      compact
                      required
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="address"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese la dirección del contratante',
                      },
                    ]}
                  >
                    <Input placeholder="Dirección" compact allowClear />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="addressNumber"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message:
                          'Ingrese el número de la dirección del contratante',
                      },
                    ]}
                  >
                    <Input
                      placeholder="N° de la dirección"
                      compact
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="city"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese la ciudad del contratante',
                      },
                    ]}
                  >
                    <Input placeholder="ciudad" compact allowClear />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="commune"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Selecciona la comuna',
                      },
                    ]}
                  >
                    <Select
                      label="Comuna"
                      placeholder="Comuna"
                      required
                      compact
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        option
                          ? ((option.children as unknown as string) || '')
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          : false
                      }
                    >
                      {communes.zurichCommunes.map((item) => (
                        <Option key={item.code} value={item.code}>
                          {item.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <p style={{ marginBottom: '12px', marginTop: '12px' }}> Asegurado </p>
          <Row gutter={[12, 12]} style={{ alignItems: 'center' }}>
            <Col xs={12}>
              <Form.Item name="insuredName" className={styles.FormItem}>
                <Input compact disabled />
              </Form.Item>
            </Col>
            {!insuranceData?.insuranceInput?.businessName && (
              <Col xs={12}>
                <Form.Item
                  name="insuredFirstSurname"
                  className={styles.FormItem}
                >
                  <Input compact disabled />
                </Form.Item>
              </Col>
            )}
            <Col xs={12}>
              <Form.Item name="insuredEmail" className={styles.FormItem}>
                <Input compact disabled />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="insuredRut" className={styles.FormItem}>
                <Input compact disabled />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="insuredPhone" className={styles.FormItem}>
                <Input compact disabled />
              </Form.Item>
            </Col>

            <Col xs={12}>
              <Form.Item
                name="insuredGender"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el género del asegurado',
                  },
                ]}
              >
                <Select
                  label="Género"
                  placeholder="Género"
                  required
                  compact
                  allowClear
                >
                  {[
                    {
                      key: 'M',
                      value: 'Masculino',
                    },
                    {
                      key: 'F',
                      value: 'Femenino',
                    },
                  ].map((item) => (
                    <Option key={item.key} value={item.key}>
                      {item.value}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="insuredAddressType"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el tipo de dirección',
                  },
                ]}
              >
                <Select
                  label="Tipo de dirección"
                  placeholder="Tipo de dirección"
                  required
                  compact
                  allowClear
                >
                  {[
                    {
                      key: 'P',
                      value: 'Particular',
                    },
                    {
                      key: 'C',
                      value: 'Cobro',
                    },
                  ].map((item) => (
                    <Option key={item.key} value={item.key}>
                      {item.value}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="insuredBirthday"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    type: 'date',
                    message: 'Ingrese la fecha de nacimiento del asegurado',
                  },
                  {
                    validator: dateFormValidator,
                  },
                ]}
              >
                <DatePicker
                  className={styles.DatePicker}
                  placeholder="Fecha de nacimiento"
                  compact
                  required
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="insuredAddress"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese la dirección del asegurado',
                  },
                ]}
              >
                <Input placeholder="Dirección" compact allowClear />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="insuredAddressNumber"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el número de la dirección del asegurado',
                  },
                ]}
              >
                <Input placeholder="N° de la dirección" compact allowClear />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="insuredCity"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese la ciudad del contratante',
                  },
                ]}
              >
                <Input placeholder="Ciudad" compact allowClear />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="insuredCommune"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Selecciona la comuna',
                  },
                ]}
              >
                <Select
                  label="Comuna"
                  placeholder="Comuna"
                  required
                  compact
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option
                      ? ((option.children as unknown as string) || '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      : false
                  }
                >
                  {communes.zurichCommunes.map((item) => (
                    <Option key={item.code} value={item.code}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={[0, 12]}>
        <Col xs={24}>
          <h3 className={styles.SubTitle}>Datos de la venta</h3>
        </Col>
        <Col xs={24}>
          <Row gutter={[12, 12]} style={{ alignItems: 'center' }}>
            <Col xs={12}>
              <Form.Item
                name="carColor"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el color del vehículo',
                  },
                ]}
              >
                <Input placeholder="Color vehículo" compact allowClear />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="carEngineId"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el n° del motor del vehículo',
                  },
                  {
                    max: 22,
                    message:
                      'El número de motor no puede tener más de 22 caracteres',
                  },
                ]}
              >
                <Input placeholder="N° motor vehículo" compact allowClear />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="carChassis"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el chasis del vehículo',
                  },
                ]}
              >
                <Input placeholder="Chasis del vehículo" compact allowClear />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="invoiceId"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el n° de factura',
                  },
                ]}
              >
                <Input placeholder="N° factura" compact allowClear />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="invoiceDate"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese la fecha de factura',
                  },
                  {
                    validator(rule, value: string, callback) {
                      if (value) {
                        const date = new Date(value);
                        const today = new Date();
                        const diffTime = Math.abs(
                          today.getTime() - date.getTime(),
                        );
                        const diffDays = Math.ceil(
                          diffTime / (1000 * 60 * 60 * 24),
                        );
                        if (diffDays > 3) {
                          callback(
                            'La factura no puede tener más de 2 días de antigüedad',
                          );
                        }
                      }
                      callback();
                    },
                  },
                ]}
              >
                <DatePicker
                  className={styles.DatePicker}
                  placeholder="Fecha de factura"
                  compact
                  required
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Alert
                showIcon
                style={{ borderRadius: '4px' }}
                icon={<InfoCircleOutlined />}
                message={
                  <p style={{ fontSize: '12px' }}>
                    La factura no puede tener más de 2 días de antigüedad.
                  </p>
                }
                type="info"
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        <Col xs={24}>
          <h3 className={styles.SubTitle}>Datos para el pago</h3>
        </Col>
        <Col xs={24}>
          <Row gutter={[12, 12]} style={{ alignItems: 'center' }}>
            <Col xs={12}>
              <Form.Item
                className={styles.FormItem}
                name="paymentDay"
                rules={[
                  {
                    required: true,
                    message: 'Ingrese un día de pago',
                  },
                ]}
              >
                <Select
                  label="Día de pago"
                  placeholder="Día de pago"
                  required
                  compact
                  allowClear
                >
                  {paymentDays.map((item) => (
                    <Option key={item.key} value={item.value}>
                      {item.value}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

ZurichModal.defaultProps = {
  insuranceData: undefined,
  customerData: undefined,
};

export default ZurichModal;
